import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, History, Sparkles, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/auth/hooks';
import { supabase } from '../../lib/supabase';
import { AIAssistant } from '../../components/ai-chat/AIAssistant';
import { ActionTimeline } from '../../components/ai-chat/ActionTimeline';

interface TokensInfo {
  available: boolean;
  tokens_limit: number | null;
  tokens_used: number;
  tokens_remaining: number | null;
  display_limit: number | null;
  display_used: number | null;
  display_remaining: number | null;
}

type Tab = 'chat' | 'history';

export default function AIAssistantPage() {
  const { user } = useAuth();
  const businessId = user?.user_metadata?.business_id;

  const [activeTab, setActiveTab] = useState<Tab>('chat');
  const [tokensInfo, setTokensInfo] = useState<TokensInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTokensInfo();
  }, [businessId]);

  const loadTokensInfo = async () => {
    if (!businessId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_remaining_tokens', {
        p_business_id: businessId
      });

      if (error) throw error;

      setTokensInfo({
        available: data.available || false,
        tokens_limit: data.tokens_limit || null,
        tokens_used: data.tokens_used || 0,
        tokens_remaining: data.tokens_remaining || null,
        display_limit: data.tokens_limit || null,
        display_used: data.tokens_used || null,
        display_remaining: data.tokens_remaining || null,
      });
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const isBlocked = tokensInfo && (!tokensInfo.available || tokensInfo.display_remaining === 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl mb-6"
            >
              <Sparkles className="h-12 w-12" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl font-bold mb-4"
            >
              העוזר החכם שלך
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              נהל את העסק שלך בשיחה חופשית - קבע תורים, חפש לקוחות, קבל תובנות ועוד
            </motion.p>

            {/* Stats Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Zap className="h-5 w-5" />
                  <span className="text-sm text-white/80">טוקנים זמינים</span>
                </div>
                <p className="text-2xl font-bold">
                  {tokensInfo?.display_remaining?.toLocaleString() || '0'}
                </p>
                <p className="text-xs text-white/60 mt-1">
                  מתוך {tokensInfo?.display_limit?.toLocaleString() || '0'}
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <MessageSquare className="h-5 w-5" />
                  <span className="text-sm text-white/80">שיחות החודש</span>
                </div>
                <p className="text-2xl font-bold">
                  {Math.ceil((tokensInfo?.display_used || 0) / 1000)}
                </p>
                <p className="text-xs text-white/60 mt-1">שיחות</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <History className="h-5 w-5" />
                  <span className="text-sm text-white/80">סטטוס</span>
                </div>
                <p className="text-2xl font-bold">
                  {tokensInfo?.available ? 'פעיל' : 'לא זמין'}
                </p>
                <p className="text-xs text-white/60 mt-1">
                  {isBlocked ? 'שדרג חבילה' : 'הכל תקין'}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('chat')}
              className={`relative py-4 px-2 font-medium transition-colors ${
                activeTab === 'chat'
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <span>צ׳אט</span>
              </div>
              {activeTab === 'chat' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                />
              )}
            </button>

            <button
              onClick={() => setActiveTab('history')}
              className={`relative py-4 px-2 font-medium transition-colors ${
                activeTab === 'history'
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="h-5 w-5" />
                <span>היסטוריה</span>
              </div>
              {activeTab === 'history' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">טוען...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'chat' && (
              <div className="bg-white rounded-2xl shadow-sm p-8 min-h-[600px]">
                <div className="max-w-4xl mx-auto">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      שאל אותי כל דבר על העסק שלך
                    </h2>
                    <p className="text-gray-600">
                      אני יכול לעזור לך לנהל תורים, לקוחות, לקבל דוחות ועוד הרבה דברים
                    </p>
                  </div>

                  {/* Render full-page chat here or instructions to use floating button */}
                  <div className="text-center text-gray-500 py-12">
                    <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg mb-2">
                      לחץ על הכפתור המרחף בפינה השמאלית התחתונה כדי להתחיל
                    </p>
                    <p className="text-sm text-gray-400">
                      או שתך להמשיך כאן בדף זה
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && <ActionTimeline />}
          </>
        )}
      </div>

      {/* Floating AI Assistant */}
      <AIAssistant tokensInfo={tokensInfo} isBlocked={!!isBlocked} />
    </div>
  );
}
